import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const UNITS = [
  { value: "perPerson", label: "/ personne" },
  { value: "perSession", label: "/ session" },
  { value: "perHour", label: "/ heure" },
  { value: "perDay", label: "/ jour" },
  { value: "perLesson", label: "/ leçon" },
  { value: "perExperience", label: "/ expérience" },
];

const AdminActivities = () => {
  const [activities, setActivities] = useState<any[]>([]);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [toggleRowId, setToggleRowId] = useState<string | null>(null);
  const [togglePending, setTogglePending] = useState(false);

  useEffect(() => {
    supabase
      .from("site_content")
      .select("id,value")
      .eq("key", "ui.activities_enabled")
      .eq("lang", "global")
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSectionEnabled(data.value !== "false");
          setToggleRowId(data.id);
        }
      });
  }, []);

  const toggleSection = async (checked: boolean) => {
    setTogglePending(true);
    setSectionEnabled(checked);
    const value = checked ? "true" : "false";
    if (toggleRowId) {
      const { error } = await supabase.from("site_content").update({ value }).eq("id", toggleRowId);
      if (error) toast.error("Erreur: " + error.message);
      else toast.success(checked ? "Activités affichées" : "Activités masquées");
    } else {
      const { data, error } = await supabase
        .from("site_content")
        .insert({ key: "ui.activities_enabled", lang: "global", value })
        .select("id")
        .single();
      if (error) toast.error("Erreur: " + error.message);
      else { setToggleRowId(data.id); toast.success(checked ? "Activités affichées" : "Activités masquées"); }
    }
    setTogglePending(false);
  };

  const load = async () => {
    const { data } = await supabase.from("activities").select("*").order("slot", { ascending: true });
    setActivities(data || []);
    const e: Record<string, any> = {};
    data?.forEach((a) => {
      e[a.id] = { title: a.title, description: a.description, price: a.price, unit: a.unit, emoji: a.emoji };
    });
    setEdits(e);
  };

  useEffect(() => { load(); }, []);

  const save = async (a: any) => {
    setSaving(a.id);
    const edit = edits[a.id];
    const { error } = await supabase.from("activities").update({
      title: edit.title,
      description: edit.description,
      price: Number(edit.price) || 0,
      unit: edit.unit,
      emoji: edit.emoji,
    }).eq("id", a.id);
    if (error) toast.error("Erreur: " + error.message);
    else toast.success(`${edit.title} mis à jour !`);
    setSaving(null);
  };

  const updateField = (id: string, field: string, value: any) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Affichage du site</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-body font-semibold">Enable Activities / Activer les Activités</p>
              <p className="font-body text-sm text-muted-foreground">
                Affiche ou masque la section "Activités / Excursions" sur la page d'accueil et dans le menu.
              </p>
            </div>
            <Switch checked={sectionEnabled} disabled={togglePending} onCheckedChange={toggleSection} />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {activities.map((a) => (
        <Card key={a.id} className="border-border hover:border-primary/30 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <span className="text-2xl">{edits[a.id]?.emoji || a.emoji}</span>
              <span>{edits[a.id]?.title || a.title}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Titre</label>
                <Input value={edits[a.id]?.title || ""} onChange={(e) => updateField(a.id, "title", e.target.value)} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Emoji</label>
                <Input value={edits[a.id]?.emoji || ""} onChange={(e) => updateField(a.id, "emoji", e.target.value)} className="w-16 text-center" />
              </div>
            </div>
            <div>
              <label className="block font-body text-xs text-muted-foreground mb-1">Description</label>
              <Textarea value={edits[a.id]?.description || ""} onChange={(e) => updateField(a.id, "description", e.target.value)} rows={2} className="resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Prix (DH)</label>
                <Input type="number" value={edits[a.id]?.price ?? 0} onChange={(e) => updateField(a.id, "price", e.target.value)} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Unité</label>
                <select
                  value={edits[a.id]?.unit || "perPerson"}
                  onChange={(e) => updateField(a.id, "unit", e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm font-body"
                >
                  {UNITS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button onClick={() => save(a)} disabled={saving === a.id} className="w-full bg-gradient-gold text-primary-foreground" size="sm">
              {saving === a.id ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </CardContent>
        </Card>
      ))}
      </div>
    </div>
  );
};

export default AdminActivities;
